import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/conversation-renderer.css";
import {
  getPostHogKey,
  getPostHogOptions,
  isPostHogDisabled,
} from "./logs/PosthogUtils.ts";
import { PostHogProvider } from "posthog-js/react";
import { ConvexReactClient, ConvexProviderWithAuth } from "convex/react";
import { initSentry } from "./lib/sentry.ts";
import {
  SessionProvider,
  useSession,
  fetchAccessToken,
} from "./hooks/use-session";

// Initialize Sentry before React mounts
initSentry();

const convexUrl = import.meta.env.VITE_CONVEX_URL as string;

// Warn if critical env vars are missing
if (!convexUrl) {
  console.warn(
    "[main] VITE_CONVEX_URL is not set; Convex features may not work.",
  );
}

const convex = new ConvexReactClient(convexUrl);

const root = createRoot(document.getElementById("root")!);

/**
 * Custom auth hook for Convex that uses our session-based auth
 */
function useSessionAuth() {
  const { isLoading, isAuthenticated } = useSession();

  return {
    isLoading,
    isAuthenticated,
    fetchAccessToken,
  };
}

/**
 * Inner providers that need SessionProvider to be available
 */
function InnerProviders({ children }: { children: React.ReactNode }) {
  return (
    <ConvexProviderWithAuth client={convex} useAuth={useSessionAuth}>
      {children}
    </ConvexProviderWithAuth>
  );
}

const Providers = (
  <SessionProvider>
    <InnerProviders>
      <App />
    </InnerProviders>
  </SessionProvider>
);

root.render(
  <StrictMode>
    {isPostHogDisabled ? (
      Providers
    ) : (
      <PostHogProvider apiKey={getPostHogKey()} options={getPostHogOptions()}>
        {Providers}
      </PostHogProvider>
    )}
  </StrictMode>,
);
